---
title: Analytics
description: From the tracking stream to the report, one careful step at a time.
---

Analytics is a CPU batch job. It replays the night's Kafka topic from the
beginning, turns the tracking stream into clean paths, measures the three
headline numbers, and writes the report. It never touches the GPU, which is
why a night can be re-analyzed cheaply while its messages are retained.

One convention underlies everything: batch sessions have no trustworthy wall
clock, so time inside the stages is seconds since the session started,
derived from frame number and frame rate. Real timestamps appear only at the
report level, anchored to the store's recording start time and timezone.

Zone and line geometry comes from one file, in floor-plan meters. Behavior,
the debounces and cooldowns, comes from store settings. A counting line with
zero length or a zero-width band is refused at load, because a line that
silently never fires is worse than a failed run.

## Consume: the topic becomes observations

The consumer reads the night's topic from offset zero and stops after a
configurable quiet period. Every run uses a fresh, throwaway consumer group
on purpose: a shared group would let Kafka split the messages between two
concurrent runs, each silently seeing half the night.

Not every message survives. Objects with no 3D position are dropped, and so
is anything at exactly the world origin, because the converter writes (0,0)
for people the tracker could not place, and letting those through plants
phantom shoppers at the origin. Non-person objects and detections below the
confidence floor are dropped too. Zero observations fails the run with a
pointed message: no data on the topic usually means the perception run
before it did not happen.

## Trajectories: observations become paths

Observations are grouped by global identity. When several cameras saw the
same person at the same instant, their positions are fused with a
confidence-weighted average, so a badly calibrated, low-confidence camera
cannot drag the position by half its error. Then three cleanups run in
order:

1. **The teleport gate.** A jump faster than the configured top walking
   speed cuts the path; that is a tracking error, not a person.
2. **The flicker filter.** Tracks shorter than the minimum duration are
   dropped.
3. **Smoothing.** A moving average smooths each path, but never across an
   occlusion gap: smoothing across a gap would drag points toward the far
   side of it, inventing positions nobody occupied.

Every number downstream is computed from these fused tracks and never from
per-camera data. Count on the global identity, always.

## Entries and exits

A crossing counts only when three things are true, and each exists because
of a real failure mode:

- **The line is a band, not a line.** An event fires only on a full pass
  from one edge of the band to the other, so jitter on the threshold cannot
  count.
- **The anchor is the feet.** Positions are ground-contact points, so a
  leaning torso cannot cross a line the person did not.
- **The track must be old enough**, and the age gate applies to the whole
  track rather than each event, because a real shopper's track is born at
  the door; gating individual events would systematically delete entries.

A per-person cooldown per line merges rapid re-crossings, so someone
shuffling in the vestibule counts once. The summary reports totals, per-line
counts, per-hour histograms and the number of distinct people who entered.

## Zone dwell

Dwell is a small state machine per person per zone. A visit opens only
after the person has been inside continuously for the enter debounce, so
boundary flicker never opens a visit. It closes only after they have been
outside for the exit grace, so a brief disappearance behind a pillar does
not split one browse into two. The two thresholds are separate because a
doorway zone wants a short enter and a browsing zone wants a forgiving
exit.

The summary reports visits, total and median dwell, and a 90th percentile
computed by nearest rank, which stays stable night over night where naive
indexing wobbles. Dwell is also the early-warning signal: when identity
tracking degrades, dwell distributions break before entry counts do.

## Journeys

Runs after dwell because it consumes the zone visits. For each person it
assembles the ordered story of the visit: when they arrived and left, the
sequence of zones with dwell in each, the distance walked, which cameras
saw them, and a simplified path for drawing on the floor plan.

Two honest measurements: gaps longer than two seconds are excluded from
walked distance, because crediting a thirty-second disappearance as fifteen
meters of walking invents a path, so distance is a lower bound whenever
gaps exist. And camera handoffs are counted so that a healthy overlap,
where two cameras see the person at once during the transition, still
counts as one handoff.

## The report

The last stage writes everything, atomically, because the control plane
serves these files while the job writes them:

- **`report.json`**, the night's summary. Its sections: provenance (which
  config and model produced these numbers, by hash), a status of `ok` or
  `attention` with a single severe flag downstream systems can poll, the
  time anchor that maps buckets to wall-clock hours, unique visitors,
  entries and exits, zone dwell, hour-by-hour occupancy, travel times
  between zone pairs, per-camera coverage, and the reconciliation block.
- **`journeys.json`**, every journey in full.
- **`events.json`**, every crossing and zone visit. This is the review
  stage's input.
- **`journeys.png`**, all paths drawn on the floor plan.

The reconciliation block is where most production counting bugs are caught,
so its checks are worth knowing by name: entries versus exits drift, lines
configured at all, counts not all zero (a night that counted nothing is a
broken line or dead calibration, not an empty store; it is the loudest
possible silent failure), the ratio of unique identities to entries, and
the peak crossing rate. Camera coverage is judged against the median
camera, not the mean, so two dead cameras out of four cannot drag the bar
down to where the survivors look sick and the dead look normal.
